import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';

import { NgForm } from "@angular/forms";
import { asyncScheduler, from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppConfig } from '../../../environments/environment';
import {Stage, Building, Route} from '../../core/models';
import {ImportFileArgs, LogImportResult, LookupService, PostProcessingImportType, PostProcessingService, RouteService} from '../../core/services';
import { StageService } from '../../core/services/stage.service';
import { TableViewQueryCriteria } from '../../shared/components/table-view/table-view-component-base';
import XRegExp from 'xregexp';

export class ImportedFileEvent {
  type: string
  file: File
  rowCount: number
}

let defaultImportTypes : PostProcessingImportType[] | null = null;

type ImportResultEntity = {
  importRowCount: number;
  created: Array<any>;
  updated: Array<any>;
  deleted: Array<any>;
  errorMsg?: string;
  uiExpanded?: boolean;
}

type OneSourceImportResult = {
  buildingResults: ImportResultEntity;
  pointResults: ImportResultEntity;
  pathResults: ImportResultEntity;
  waypointResults: ImportResultEntity;
  routeResults: ImportResultEntity;

  rejections: any; // not used... just making typescript happy
}

type VolunteerImportResult = {
  volunteerResults: ImportResultEntity;
  handsetResults: ImportResultEntity;

  rejections: any; // not used... just making typescript happy
}

type ImportCategory = 'one-source' | 'volunteer' | 'baro-log' | null;
type ImportTracker =  {
  file: File;
  importCategory: ImportCategory;
  importType: PostProcessingImportType;
  inProgress?: boolean
  percentComplete?: number;
  result?: LogImportResult | OneSourceImportResult | VolunteerImportResult | null;
  successMsg?: string;
  errorMsg?: string;
  uiExpanded?: boolean;
  attempted?: boolean;
};

@Component({
  selector: 'app-import-file',
  templateUrl: './import-file.component.html',
  styleUrls: ['./import-file.component.css']
})
export class ImportFileComponent implements OnInit {

  @ViewChild('importFileForm') importFileForm: NgForm;
  @ViewChild('fileInput', { static: false }) fileInput?: ElementRef;
  @Output('fileImported') fileImported = new EventEmitter<ImportedFileEvent>()
  @Output('fileImportFailed') fileImportFailed = new EventEmitter<{fileName:string,error:any}>()

  importFileTrackers:ImportTracker[] = [];
  importNotStartedCnt = 0;
  importInProgressCnt = 0;
  cumulativePercentComplete?: number = 0;
  selectedImportType: PostProcessingImportType
  selectedStage: Stage
  selectedBuilding: Building
  selectedRoute: Route
  stageQueryRequest: Observable<Stage[]>
  routesQueryRequest: Observable<Route[]>
  buildingQueryRequest: Observable<Building[]>

  importTypes: PostProcessingImportType[]

  bAbortOnRejectedRows = true;
  bBaroUtcToBuildingLocalTime = true;

  constructor(
    public postProcessingService: PostProcessingService,
    public stageService: StageService,
    public routeService:RouteService,
    public lookupService: LookupService) { }


  ngOnInit(): void {

    this.importTypes = defaultImportTypes || this.postProcessingService.ImportTypes

    asyncScheduler.schedule( async () => {

      let fileImportConfig = AppConfig.fileImport
      try {
        const fileImportOverrides = (await this.lookupService.rxCollection.findOne('fileImport').exec())?.toJSON() || {}
        fileImportConfig = { ...fileImportConfig, ...fileImportOverrides }
      } catch ( error: any ) {
        console.error(error)
      }

      this.importTypes = this.postProcessingService.ImportTypes

      let {carrierIdRegex,titleRegex} = fileImportConfig
      const carrierIdRegExp = XRegExp(carrierIdRegex||".*")
      const titleRegExp = XRegExp(titleRegex||".*")
      this.importTypes = this.importTypes.filter( importType =>
        (importType.carrierId&&carrierIdRegExp.test(importType.carrierId)) || titleRegExp.test(importType.name) )
        defaultImportTypes = this.importTypes
      console.log('import-file args filter criteria:', { carrierIdRegex, titleRegex, result: this.importTypes})
    })

    this.stageQueryRequest = from(this.stageService.rxCollection.find().$)
    this.routesQueryRequest=from(this.routeService.rxCollection.find().$)

    const bldgQueryCriteria : TableViewQueryCriteria = { sorts: [{key:'id',order:'ASC'}] }
    this.buildingQueryRequest = from(this.postProcessingService.findBuildings(bldgQueryCriteria)).pipe(
      map( bldgResp =>  bldgResp.rows )
    )
  }

  async onFileInputChanged(event: Event) {
    console.log('onFileInputChanged',event)
    const inputFiles = Array.from((event.target as HTMLInputElement).files);
    this.importFileTrackers = inputFiles.map( f => ({
      file:f,
      importCategory: this.selectedImportType?.name as ImportCategory,
      importType: this.selectedImportType,
      result:null,
      inProgress:false,
      percentComplete:0,
      importSuccessMsg:'',
      uiExpanded: false      
    }));
    const allowedFileTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain'
    ]
    for ( const importTracker of this.importFileTrackers ) {
      const fileType = (importTracker.file?.type || '').toLowerCase()
      if ( !allowedFileTypes.includes(fileType) ) {
        importTracker.errorMsg = "Unsupported import file format (type="+fileType+", file="+importTracker.file.name+")"
      }
    }
    this.updateTrackerStats();
  }

  async onImportFiles() {
    for( const importTracker of this.importFileTrackers ) {
      try {
        await this.importFile(importTracker)
      } catch ( error ) {
        console.error(error)
      }
      this.updateTrackerStats();
    }
  }

  updateTrackerStats() {
    this.importInProgressCnt = this.importFileTrackers.reduce( (cnt,importTracker) => importTracker.inProgress ? cnt+1 : cnt, 0 );
    this.importNotStartedCnt = this.importFileTrackers.reduce( (cnt,importTracker) => (!importTracker.result&&!importTracker.inProgress)? cnt+1 : cnt, 0 );
    this.cumulativePercentComplete = this.importFileTrackers.reduce( (cnt,importTracker) => cnt + (importTracker.inProgress ? importTracker.percentComplete : 0), 0 ) / this.importFileTrackers.length;
  }

  async importFile(importTracker: ImportTracker) {
    importTracker.successMsg = '';
    importTracker.inProgress = true;
    importTracker.percentComplete = 0;
    importTracker.errorMsg = undefined;
    importTracker.result = undefined;
    importTracker.uiExpanded = true;
    const progressSetter = (percentComplete:number) => {
      importTracker.percentComplete = percentComplete
    }
    this.updateTrackerStats();
    const importFile = importTracker.file;
    try {
      try {
        // this should fail if file was modified after being selecting in html form (way to inform user to re-select file)
        await importFile.arrayBuffer();  // TODO. just one read one byte or something lighter
      } catch ( error ) {
        console.error(error)
        importTracker.errorMsg = 'Uploaded file is not accessible or has been modified. Reset form input and try again.';
        throw new Error(importTracker.errorMsg);
      }
      const importFileArgs: ImportFileArgs = {
        abortOnRejections: this.selectedImportType?.supportsRejections && this.bAbortOnRejectedRows,
        baroUtcToBuildingLocalTime: this.bBaroUtcToBuildingLocalTime,
        file: importFile,
        importType: this.selectedImportType,
        stage: this.selectedStage,
        route: this.selectedRoute,
        building: this.selectedBuilding,
        progressSetter: progressSetter
      }
      importTracker.importType = this.selectedImportType;
      function nullOutEmptySections<T>(sectionNames: Array<keyof T>, result: T): void {
        sectionNames.forEach(sectionName => {
            const section = result[sectionName] as unknown as ImportResultEntity;
            if (section && !section.created?.length && !section.updated?.length && !section.deleted?.length && !section.errorMsg) {
                result[sectionName] = undefined as any;
            }
        });
      }
      if(this.selectedImportType.urlSubPath === 'oneSource') {
        importTracker.importCategory='one-source';
        const resp = await this.postProcessingService.importFile(importFileArgs);
        const result = resp.data as OneSourceImportResult;
        const resultSections:(keyof OneSourceImportResult)[] = ['buildingResults', 'pointResults', 'pathResults', 'waypointResults', 'routeResults'];
        nullOutEmptySections(resultSections, result);
        const errorCnt = resultSections.map(key=>result[key]).filter(r=>!!r?.errorMsg).length;
        if ( errorCnt > 0 ) {
          importTracker.errorMsg = `${errorCnt} error(s)`;
        }
        importTracker.result = result;
        this.oneSourceMsgBuilder(importTracker);
      } else if(this.selectedImportType.urlSubPath === 'volunteer') {
        importTracker.importCategory = 'volunteer';
        const resp = await this.postProcessingService.importFile(importFileArgs);
        const result = resp.data as VolunteerImportResult;
        const resultSections:(keyof VolunteerImportResult)[] = ['volunteerResults', 'handsetResults'];
        nullOutEmptySections(resultSections, result);
        const errorCnt = resultSections.map(key=>result[key]).filter(r=>!!r?.errorMsg).length;
        if ( errorCnt > 0 ) {
          importTracker.errorMsg = `${errorCnt} error(s)`;
        }
        importTracker.result = result;
        this.volunteerMsgBuilder(importTracker)
      } else {
        if ( importFileArgs.importType === this.postProcessingService.baroCallLogImport ) {
          importTracker.importCategory = 'baro-log';
          importFileArgs.baroUtcToBuildingLocalTime = this.bBaroUtcToBuildingLocalTime;
        }
        const resp= await this.postProcessingService.importLogFile(importFileArgs);
        if ( resp.code != 0 ) {
          console.error(resp);
          importTracker.errorMsg = resp.msg || ('Import returned error code '+resp.code);
          return;
        }
        importTracker.result = resp.data;
        const counts = importTracker.result.counts;
        if ( counts.rejected ) {
          importTracker.errorMsg = importFileArgs.abortOnRejections ? 'Import aborted (no rows imported).  ' : '';
          importTracker.errorMsg += `Rejected ${counts.rejected} `;
          const duplicateCnt = importTracker.result?.rejections?.reduce((cnt,r)=>cnt+(r.guardType==='duplicate'?1:0),0);
          if ( duplicateCnt === counts.rejected ) {
            importTracker.errorMsg += 'duplicate(s)' 
          } else {
            importTracker.errorMsg += 'record(s)'
          }
          importTracker.errorMsg += ` from ${importFile?.name} (${this.selectedImportType.name}).`;
          if ( duplicateCnt !== counts.rejected ) {
            if ( counts.rejected === 1 ) {
              importTracker.errorMsg += ` ${importTracker.result.rejections[0].message}`
            } else {
              importTracker.errorMsg += ` Download json report for individual error messages.`
            }
          }
        }
        if ( !importFileArgs.abortOnRejections || (counts.imported || counts.skipped) ) {
          importTracker.successMsg = `Imported ${counts.imported} records from ${importFile?.name} (${this.selectedImportType.name})`
          if ( counts.skipped ) {
            importTracker.successMsg += ` Skipped ${counts.skipped} records.`
          }
        }
        this.fileImported.emit({type:this.selectedImportType?.carrierId,file:importFile,rowCount:counts.imported})
      }
    } catch ( error ) {
      importTracker.errorMsg = this.postProcessingService.extractErrorMsg(error)
      this.fileImportFailed.emit({fileName:importFile?.name,error});
    }
    importTracker.percentComplete = 0
    importTracker.inProgress = false
    importTracker.attempted = true;
    this.updateTrackerStats();
  }

  asOneSourceResult(result:any) {
    return result as OneSourceImportResult;
  }
  asVolunteerResult(result:any) {
    return result as VolunteerImportResult;
  }

  reset() {
    this.bAbortOnRejectedRows = true;
    this.importFileTrackers = [];
    // don't clear all form inputs... just the file input
    if ( this.fileInput ) {
      this.fileInput.nativeElement.value = '';
    }
  }

  oneSourceMsgBuilder(importTracker: ImportTracker) {
    const result = importTracker.result as OneSourceImportResult;
    if(result.buildingResults?.importRowCount == 0 && result.pointResults?.importRowCount == 0 && result.pathResults?.importRowCount == 0 && result.waypointResults?.importRowCount == 0 && result.routeResults?.importRowCount == 0) {
      importTracker.successMsg = 'One Source Master Tracker Already Up to Date'
    }
  }

  volunteerMsgBuilder(importTracker: ImportTracker) {
    const result = importTracker.result as VolunteerImportResult;

    if(result.volunteerResults?.importRowCount == 0 && result.handsetResults?.importRowCount == 0) {
      importTracker.successMsg = 'Volunteers already Up to Date'
    }
  }

  bDownloading = false;

  // shows everything we have about errors (db entity, workbook row, error message)
  downloadRejectedJson(importTracker:ImportTracker) {
    this.bDownloading = true
    try {
      const data = JSON.stringify((importTracker.result as LogImportResult).rejections,null,2);
      const blob = new Blob([data], {type: 'application/octet-stream'})
      saveAs(blob, `rejected_${this.importFileTrackers[0].file.name?.replace(/[.]\w*$/,'')??(new Date().toISOString())}.json`);
    } catch (error) {
      console.error(error)
      importTracker.errorMsg = this.postProcessingService.extractErrorMsg(error);
    }
    this.bDownloading = false
  }

  // Rejected rows (csv with last column being the error message)
  downloadRejectedRows(importTracker:ImportTracker) {
    this.bDownloading = true
    try {
      const result = importTracker.result as LogImportResult;
      const csvRows = [];
      const csvColNames = result.wsHeaders ?? Object.keys(result.rejections[0]);
      csvColNames.push('Testbed Import Result')
      csvRows.push( csvColNames );
      const replacer = (key, value) => (value === null ? "" : value);

      for( let i = 0; i < result.rejections.length; i++ ) {
        const rowMeta = result.rejections[i];
        const csvRow = rowMeta.wsRow;
        if ( !csvRow ) {
          console.warn("missing rejected import item worksheet source row", rowMeta);
          continue;
        }
        csvRow.push(rowMeta.message);
        csvRows.push(csvRow.map(fieldVal=>JSON.stringify(fieldVal, replacer)));
      }
      let csvStrRows = csvRows.map(csvRow=>csvRow.join(','));
      let strCsv = csvStrRows.join('\r\n');
      var blob = new Blob([strCsv], {type: 'text/csv' })
      saveAs(blob, `rejected_${this.importFileTrackers[0].file.name?.replace(/[.]\w*$/,'')??(new Date().toISOString())}.csv`);
    } catch ( error: any ) {
      console.error(error);
    }
    this.bDownloading = false
  }

}
